import { useCallback, useEffect, useMemo, useState } from "react";
import { ConditionGroup } from "stores/data/insightAlerts/insightAlertModel";
import { useStores } from "stores/setup/use-store";
import i18n from "../../../../i18n";
import { formatConditions } from "../util/formatConditionGroups";
import { getTemplateMessages } from "../util/getTemplateMessages";

export function useAlertEditTemplates() {
  const {
    dataStores: { insightAlertStore },
  } = useStores();

  const [selectedTemplateType, setSelectedTemplateType] = useState<string>(
    insightAlertStore.operationRuleAlert?.alertType || "extreme_wind"
  );
  const [alertTitle, setAlertTitle] = useState<string>("");
  const [alertDescription, setAlertDescription] = useState<string>("");

  const TEMPLATE_MESSAGES = useMemo(() => getTemplateMessages(), []);

  const defaultTemplate = TEMPLATE_MESSAGES["extreme_wind"];

  useEffect(() => {
    if (!selectedTemplateType) return;

    const { operationRuleAlert, alertTemplates } = insightAlertStore;
    const notifications = operationRuleAlert?.notifications;
    const editedTemplateType = alertTemplates.find(
      (t) => t.id === operationRuleAlert?.alertTemplateId
    )?.alertType;

    // TEMPLATE_MESSAGES only carries the canonical alert types
    // (extreme_wind, port_closure, extreme_rain). For any other alertType
    // (e.g. rough_sea) fall back to the default template so we never set
    // title/description from undefined.
    const templateMessage =
      TEMPLATE_MESSAGES[selectedTemplateType] ?? defaultTemplate;

    let message;
    if (editedTemplateType !== selectedTemplateType) {
      message = templateMessage;
    } else {
      message = notifications?.[0] ?? templateMessage;
    }

    if (!message) return;
    setAlertTitle(message.title);
    setAlertDescription(message.description);
  }, [selectedTemplateType, insightAlertStore, TEMPLATE_MESSAGES]);

  const alertTemplateTypeFilter = useMemo(() => {
    return insightAlertStore.alertTypes;
  }, [insightAlertStore.alertTypes]);

  const conditionsForExampleTemplate = useCallback(
    (conditionGroups?: ConditionGroup[]) => {
      return formatConditions(
        conditionGroups ||
        insightAlertStore.operationRuleAlert?.conditionGroups,
        i18n.language as "pt-BR" | "en"
      )
    },
    [insightAlertStore.operationRuleAlert?.conditionGroups]
  );

  const filterOption = useCallback(
    (input: string, option?: { label?: string; value?: string }) => {
      return (
        option?.label?.toString().toLowerCase().includes(input.toLowerCase()) ??
        false
      );
    },
    []
  );

  return {
    selectedTemplateType,
    setSelectedTemplateType,
    alertTitle,
    setAlertTitle,
    alertDescription,
    setAlertDescription,
    defaultTemplate,
    alertTemplateTypeFilter,
    conditionsForExampleTemplate,
    filterOption,
  };
}
